import React, { useEffect, useState } from "react";
import {
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  LinearProgress,
  Box,
  Paper,
  Checkbox,
  Link,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { parseJwt } from "helpers/Jwt";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import { Checkmark, LogoHorizontal } from "components/Icons";
import { useForm, FormState, schemas } from "helpers/FormHelpers";
import { Config } from "Config";
import Pendo from "helpers/Pendo";
import { useAppDispatch } from "redux/hooks";
import { getSession, setUserInfo } from "redux/slices/session";
import { backend, UserInfo } from "Backend";
import theme from "theme";

const getSchema = (exists: boolean) => {
  return {
    firstName: schemas.nonEmpty("First name"),
    lastName: schemas.nonEmpty("Last name"),
    password: exists ? schemas.basicOptional : schemas.password,
    confirmPassword: {
      equality: "password",
    },
  };
};

type Field = "password" | "confirmPassword" | "firstName" | "lastName";
const initial: FormState<Field> = {
  isValid: false,
  values: { firstName: "", lastName: "", password: "", confirmPassword: "" },
  touched: { firstName: false, lastName: false, password: false, confirmPassword: false },
  errors: {},
};

interface OnboardingToken {
  email: string;
}

interface FormWrapperProps {
  children?: React.ReactNode;
}

const FormWrapper = (props: FormWrapperProps) => {
  const isMobile = useMediaQuery("(min-width:600px");

  if (isMobile) {
    return (
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          backgroundColor: "white",
          padding: theme.spacing(2, 3),
        }}>
        {props.children}
      </Paper>
    );
  } else {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          backgroundColor: "white",
          padding: theme.spacing(2, 3),
        }}>
        {props.children}
      </Box>
    );
  }
};

const Onboarding = () => {
  const params = new URLSearchParams(document.location.search);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const rawToken = params.get("token") || "";
  const exists = params.get("exists") === "true";
  let token: OnboardingToken;
  try {
    token = parseJwt<OnboardingToken>(rawToken);
  } catch (e) {
    console.error(e);
    token = { email: "" };
  }

  // const [formState, setFormState] = React.useState<FormState>(initializeForm());

  const [showPassword, setShowPassword] = useState(false);

  const [submitError, setSubmitError] = useState("");

  const [loading, setLoading] = useState(false);

  const [tosChecked, setTosChecked] = useState(false);

  const form = useForm<Field>(initial, getSchema(exists));
  const {
    formStateHandle: [formState, _setFormState],
    isGood,
    hasError,
    errorText,
    handleChange,
  } = form;

  useEffect(() => {
    if (token.email === "") {
      setSubmitError("Expired or invalid invitation");
      return;
    }
    setLoading(true);
    backend.checkOnboarded(rawToken).then((result) => {
      setLoading(false);
      backend.mapSuccess(result, (response) => {
        const pending = response.pendingUser;

        switch (response.message) {
          case "valid":
            if (pending?.firstName) {
              form.updateField("firstName")(response.pendingUser?.firstName);
            }
            if (pending?.lastName) {
              form.updateField("lastName")(response.pendingUser?.lastName);
            }
            break;
          case "already_onboarded":
            navigate("/signin");
            break;
          case "invalid_token":
            setSubmitError("Expired or invalid invitation");
            break;
        }
      });
    });
  }, [rawToken]);

  const handleSubmit: React.FormEventHandler = async (event) => {
    event.preventDefault();
    setLoading(true);
    setSubmitError("");
    if (rawToken) {
      const response = await backend.onboardUser(
        rawToken,
        formState.values.firstName.trim(),
        formState.values.lastName.trim(),
        !exists ? formState.values.password : undefined
      );
      setLoading(false);
      switch (response.tag) {
        case "success": {
          const session = await dispatch(getSession())
            .unwrap()
            .catch(() => {
              setSubmitError("There was an error fetching your session");
            });

          const info: UserInfo = response.value;

          if (session) {
            Pendo.initialize(session, parseInt(Config.version), info.userType, info.firstName, info.lastName);
            Pendo.event("user_onboarded", {});
            dispatch(setUserInfo(info));
            navigate("/lens");
          }

          break;
        }
        case "error": {
          setSubmitError("There was an error contacting the server");
          break;
        }
        case "unauthorized": {
          setSubmitError("Invalid or expired token");
          break;
        }
        default:
          setSubmitError("There was an error contacting the server");
          break;
      }
    }
  };

  if (!rawToken) return <div>Missing Token</div>;
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.grey[50],
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        width: "100%",
      }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          my: 2,
          gap: 2,
        }}>
        <LogoHorizontal />
      </Box>
      <form
        style={{
          width: "400px",
        }}
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}>
        <FormWrapper>
          <TextField
            id="email"
            name="email"
            label="Email"
            size="medium"
            helperText="Already set"
            sx={{ minHeight: "80px", mt: 4 }}
            disabled={true}
            value={token.email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            id="firstName"
            name="firstName"
            label="First name"
            autoFocus
            size="medium"
            placeholder="First name"
            sx={{ minHeight: "80px" }}
            value={formState.values.firstName}
            onChange={handleChange}
            helperText={errorText("firstName")}
            error={hasError("firstName")}
          />
          <TextField
            id="lastName"
            name="lastName"
            label="Last name"
            placeholder="Last name"
            size="medium"
            sx={{ minHeight: "80px" }}
            value={formState.values.lastName}
            onChange={handleChange}
            helperText={errorText("lastName")}
            error={hasError("lastName")}
          />
          {!exists && (
            <>
              <TextField
                id="password"
                name="password"
                label="Password"
                size="medium"
                placeholder="Choose a password"
                helperText={errorText("password")}
                sx={{ minHeight: "80px" }}
                type={showPassword ? "text" : "password"}
                value={formState.values.password}
                onChange={handleChange}
                error={hasError("password")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        tabIndex={-1}
                        onClick={() => setShowPassword(!showPassword)}
                        size="large">
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                id="confirmPassword"
                name="confirmPassword"
                label="Confirm Password"
                placeholder="Repeat password"
                size="medium"
                sx={{ minHeight: "80px" }}
                type={showPassword ? "text" : "password"}
                value={formState.values.confirmPassword}
                onChange={handleChange}
                error={hasError("confirmPassword")}
                helperText={errorText("confirmPassword")}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">{isGood("confirmPassword") && <Checkmark />}</InputAdornment>
                  ),
                }}
              />
            </>
          )}

          <Box display="flex" alignItems="center" marginBottom={5}>
            <Checkbox checked={tosChecked} onChange={(evt) => setTosChecked(evt.target.checked)} />
            <Typography variant="subtitle1">
              I accept the{" "}
              <Link
                sx={{
                  ...theme.mixins.linkLike,
                }}
                target="_blank"
                href={Config.termsOfServiceUrl}>
                Terms of Service
              </Link>
            </Typography>
          </Box>

          <Button
            sx={{
              p: 3,
            }}
            variant="contained"
            color="primary"
            type="submit"
            disabled={!formState.isValid || !tosChecked}>
            Create My Account
          </Button>

          <div
            style={{
              minHeight: "30px",
            }}>
            {submitError && (
              <Typography color="error" align="center" variant="body4">
                {submitError}
              </Typography>
            )}
            <Box visibility={loading ? "visible" : "hidden"}>
              <LinearProgress />
            </Box>
          </div>
        </FormWrapper>
      </form>
    </Box>
  );
};

export default Onboarding;
